import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { tracker } from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { getCookie } from './utils/cookie';

export const exportedTracker = tracker;
import 'typeface-roboto';
import styles from './index.module.css';
import App from './App/App';
import * as serviceWorker from './serviceWorker';
import { BrowserRouter } from 'react-router-dom';
import store from './store';
import NotificationModalContainer from './components/notification/NotificationModalContainer';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import SpinnerContainer from './components/spinner/SpinnerContainer';
import NetworkErrorModalContainer from './components/errorModals/NetworkErrorModalContainer';
import ErrorModalContainer from './components/errorModals/ErrorModalContainer';
import TermsAndConditionsModalContainer from './components/termsAndConditions/TermsAndConditionsModalContainer';
import { MuiThemeProvider, createTheme } from '@material-ui/core';

const inputBkgColor = '#fff';

const isStage = process.env.REACT_APP_STAGE === 'stage';

if (isStage) {
  tracker.configure({
    projectKey: 'woLSAmpOCOjctoi3Xlp9',
    ingestPoint: 'https://weblytics.orgadynamics.com/ingest',
    network: {
      capturePayload: false,
      failuresOnly: false,
      ignoreHeaders: ['Authorization', 'Cookie'],
    } as any,
  });
  tracker.use(trackerAssist());
}

const OpenReplayTracker: React.FC = () => {
  React.useEffect(() => {
    if (isStage) {
      const userID = getCookie('ppr') || getCookie('id');
      const loginType = getCookie('logintype');
      if (loginType !== 'DashTV') {
        tracker.start();
        if (userID) {
          tracker.setUserID(userID);
        }
        
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            if (user.stations) tracker.setMetadata('station', user.stations);
            if (user.email) tracker.setMetadata('email', user.email);
            const name = ((user.firstName || '') + ' ' + (user.lastName || '')).trim();
            if (name) tracker.setMetadata('name', name);
          } catch (e) {
            // Ignore parse errors
          }
        } else {
          const station = getCookie('station');
          const email = getCookie('username');
          const name = getCookie('name');
          
          if (station) tracker.setMetadata('station', station);
          if (email) tracker.setMetadata('email', email);
          if (name) tracker.setMetadata('name', name);
        }
      }
    }
  }, []);

  return null;
};

const theme = createTheme({
  overrides: {
    MuiPaper: {
    },
    MuiInputBase: {
      root: {
        borderRadius: 'var(--ip-br)',
        backgroundColor: inputBkgColor,
        fontSize: '12px',
        '&$disabled': {
          backgroundColor: 'rgba(0, 0 , 0 ,0.05 )',
          '&:hover':{
            outline: 'none'
          },
        },
        '&:before, &:after':{
          borderBottom:'none'
        },
        MuiSelect:{
          select:{
            '&$disabled': {
              backgroundColor: 'rgba(0, 0 , 0 ,0.05 )',
            },
          }
        }
      },
      input: {
        padding: '7px 10px ',
        fontSize: 'var(--input-fs)',
      },
    },
    MuiSelect:{
      select:{
        borderRadius: 'var(--ip-br) !important',
        backgroundColor: inputBkgColor,
        fontSize: '12px',
        paddingRight: '20px',
        paddingLeft: '12px',
        lineHeight: 'normal',
        '&$disabled': {
          backgroundColor: 'transparent',
        },
        '&:focus': {
          borderRadius: 'var(--ip-br)',
          backgroundColor: 'transparent',
        }
      },
      selectMenu:{
        paddingRight: '20px',
        paddingLeft: '12px',
        '&:focus': {
          borderRadius: 'var(--ip-br)',
          backgroundColor: 'transparent',
        }
      },
      icon: {
        color: 'var(--primaryblue)',
        borderRadius: '50%',
        padding: '4px 8px',
        fontSize: '18px',
        boxSizing: 'content-box',
      },
      iconOutlined:{
        right: 0,
        top: 0,
      }
    },
    MuiNativeSelect:{
      select:{
        paddingRight: '20px',
        paddingLeft: '12px',
        '&:focus': {
          borderRadius: 'var(--ip-br)',
          backgroundColor: 'transparent',
        }
      }
    },
    MuiOutlinedInput: {
      notchedOutline:{
        borderRadius: 'var(--ip-br) !important',
        borderColor: 'transparent',
        paddingRight: '0',
        '&$disabled': {
          backgroundColor: 'rgba(0, 0 , 0 ,0.05 )',
        },
        '&:before, &:after':{
          borderBottom:'none !important'
        },
      },
      adornedEnd:{
        paddingRight: '0px',
      },
      inputAdornedEnd: {
        paddingRight: '0px !important'
      },
      input: {
        padding: '7px 10px ',
        fontSize: 'var(--input-fs)',
      },
      multiline: {
        // padding: '12px 10px ',
        padding: '7px 10px ',
      }
    },
    MuiFormControl:{
      root:{
        '& dateInput ': {
          '& button':{
            backgroundColor: 'var(--primaryblue)',
            borderRadius: '50%',
            marginRight: '-1px',
            '&:hover,&:focus':{
              backgroundColor: 'var(--primaryblue)',
              borderRadius: '50%',
            }
          }
        },
        '& timeInput ': {
          '& svg':{
            backgroundColor: 'var(--primaryblue)',
            borderRadius: '50%',
          }
        }, 
      }
    },
    MuiInputAdornment:{
      positionEnd:{
        '& button':{
          borderRadius: '50%',
          padding: '0',
        },
        '& svg':{
          color: 'var(--primaryblue)',
          padding: '4px 8px',
          fontSize: '18px',
          boxSizing: 'content-box'
        }   
      },
    },
    MuiDialogTitle:{
      root:{
        paddingBottom:'0px',
        fontFamily: 'inherit'
      },
    },
    MuiList:{
      root:{
        padding: '10px'
      }
    },
    MuiTableCell: {
      head: {
        lineHeight: '1px',
      },
      body: {
        PrivateSwitchBase: {
          root: {
            paddingRight: 0,
          }
        },
        '&:first-child': {
          borderRadius:'10px 0 0 10px'
        },
        '&:last-child': {
          borderRadius:'0 10px 10px 0'
        },
        '&:last-child .MuiIconButton-root': {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          '&:only-child': {
            paddingRight: 0,
          },
          '&:hover':{
            backgroundColor: 'transparent',
          }
        },
        '& .MuiIconButton-root .MuiFormControlLabel-root': {
          marginRight: '0',
        },
        '& button label': {
          marginRight: '0',
        },
        '& label': {
          marginRight: '0',
        },
        '& .MuiSvgIcon-root': {
          fontSize: '18px',
          boxSizing: 'content-box'
        }
      },
      
    },
    MuiTableRow:{
      root: {
        '&.MuiTableCell-root:first-child': {
          borderRadius:'10px 0 0 10px'
        },
        '&.MuiTableCell-root:last-child': {
          borderRadius:'0 10px 10px 0'
        },
      }
    },
    MuiCheckbox:{
      root:{
        '&~span': {
          fontSize: '13px'
        },
        '&+span': {
          fontSize: '13px'
        },
      }
    },
    MuiRadio:{
      root:{
        '&~span': {
          fontSize: '13px'
        },
        '&+span': {
          fontSize: '13px'
        },
      }
    },
    MuiButton: {
      root:{
        padding: '8px 16px',
      },
      label: {
        fontSize: '13px'
      }     
    },
    MuiTypography: {
      body1: {
        fontFamily: 'inherit',
      }
    },
    MuiFormControlLabel:{
      root:{
        '&.Mui-disabled, & .Mui-disabled': {
          backgroundColor: 'transparent',
          '& p':{
            color: 'grey',
          }
        }
      }
    },
  },
  
  palette: {
    primary: {
      main: 'rgb(9, 43, 74)'
    }
  },
});

// Matomo Tag Manager: Set user ID if already logged in
const storedUser = localStorage.getItem('user');
if (storedUser) {
  try {
    const user = JSON.parse(storedUser);
    if (user.userId || user.email) {
      window._mtm = window._mtm || [];
      window._mtm.push({
        event: 'user_login',
        userId: user.userId ? user.userId.toString() : user.email
      });
      
      // Set custom dimensions
      window._paq = window._paq || [];
      window._paq.push(['setCustomDimension', 2, user.email || '']);
      window._paq.push(['setCustomDimension', 1, ((user.firstName || '') + ' ' + (user.lastName || '')).trim()]);
      window._paq.push(['setCustomDimension', 3, user.stations || '']);
    }
  } catch (e) {
    // Ignore JSON parse errors
  }
}

ReactDOM.render(
<MuiThemeProvider theme={theme}>
  <MuiPickersUtilsProvider utils={MomentUtils}>
    <Provider store={store}>
      <BrowserRouter>
        <div className={styles.flexWrapper}>
          <OpenReplayTracker />
          <App />
          <NotificationModalContainer></NotificationModalContainer>
          <SpinnerContainer />
          <NetworkErrorModalContainer />
          <ErrorModalContainer />
          <TermsAndConditionsModalContainer />
        </div>
      </BrowserRouter>
    </Provider>
  </MuiPickersUtilsProvider>
</MuiThemeProvider >, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
